// Force recompile - updated at $(date)
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import { combineFormInstances, splitFormDataByInstance, extractStudentInfo } from '../../utils/formCombiner';
import { syncSharedFields } from '../../utils/sharedFieldMapper';
import '../superadmin/AddDistrict.css';

const NewStudentForm = () => {
  const navigate = useNavigate();
  const { studentIntakeId } = useParams();
  const [studentIntake, setStudentIntake] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [combinedFormDefinition, setCombinedFormDefinition] = useState(null);
  const [formInstanceMap, setFormInstanceMap] = useState({});
  const [formInstances, setFormInstances] = useState([]);
  const [allFormInstances, setAllFormInstances] = useState([]); // Store all instances before filtering
  const [formValues, setFormValues] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [savingProgress, setSavingProgress] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [autofillEnabled, setAutofillEnabled] = useState({}); // Track autofill enabled/disabled per form instance
  const [scrollPosition, setScrollPosition] = useState(0);
  const [siblingAutofillMessage, setSiblingAutofillMessage] = useState('');
  const [parentHelperData, setParentHelperData] = useState({}); // Parent-level helper data for reuse across students
  const [deletedAuthorizedPersons, setDeletedAuthorizedPersons] = useState(new Set()); // Track manually deleted authorized persons
  const [manuallyChangedHelperFields, setManuallyChangedHelperFields] = useState(new Set()); // Track manually changed helper form fields


  // Helper function to check if a form instance gets autofilled from helper form
  const isFormAutofilled = (instance) => {
    const formName = (instance.instance_name || instance.master_form_name || '').toLowerCase();
    return (
      formName.includes('acceptable use agreement') ||
      formName.includes('primary parent household') ||
      formName.includes('new student') ||
      formName.includes('residency') ||
      formName.includes('emergency') ||
      formName.includes('consent') ||
      formName.includes('record')
    );
  };
  const [helperFormData, setHelperFormData] = useState({}); // Store helper form field values
  const [householdInformation, setHouseholdInformation] = useState({}); // Store household information for autofill
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const [hasUserStartedFilling, setHasUserStartedFilling] = useState(false);
  const [hasRunInitialAutofill, setHasRunInitialAutofill] = useState(false);
  const previousFormValuesRef = useRef({});
  const siblingsAutofillRef = useRef(null);
  const latestFormValuesRef = useRef(null); // Captures latest values from handleFormChange before state commits

  // Track which helper form fields came from student intake data
  const [fieldsFromStudentIntake, setFieldsFromStudentIntake] = useState(new Set());

  // Function to determine if a field should be read-only (came from student intake)
  const isFieldReadOnly = (fullFieldName, fieldName) => {
    // Extract the simple field name from the full field name
    // Format is: "uuid_fieldname" - we want just "fieldname"
    const simpleFieldName = fullFieldName.split('_').slice(1).join('_');

    // Check if this field came from student intake data
    return fieldsFromStudentIntake.has(simpleFieldName);
  };




  // Load household information for reuse across students
  const loadHouseholdInformation = async () => {
    try {
      const response = await axios.get('/api/form-submissions/household-information');
      const householdData = response.data || {};

      // Combine separate first/last names to match helper form expectations
      const combinedData = { ...householdData };
      if (householdData.mother_guardian_first_name || householdData.mother_guardian_last_name) {
        combinedData.mother_guardian_name = `${householdData.mother_guardian_first_name || ''} ${householdData.mother_guardian_last_name || ''}`.trim();
      }
      if (householdData.father_guardian_first_name || householdData.father_guardian_last_name) {
        combinedData.father_guardian_name = `${householdData.father_guardian_first_name || ''} ${householdData.father_guardian_last_name || ''}`.trim();
      }

      // Store household information for autofill
      setHouseholdInformation(combinedData);

      setParentHelperData(combinedData);
    } catch (error) {
      console.warn('Household information not available yet - using empty data');
      // Reset household information on error
      setHouseholdInformation({});
      // Fallback to empty object if endpoint doesn't exist yet or fails
      setParentHelperData({});
    }
  };

  const fetchStudentIntake = useCallback(async () => {
    try {
      const response = await axios.get(`/api/student-intake/my-intake/${studentIntakeId}`);
      setStudentIntake(response.data);
    } catch (error) {
      console.error('Failed to fetch student intake:', error);
      console.error('Error details:', error.response?.data);
      setError('Failed to load student information');
    }
  }, [studentIntakeId]);

  // Fetch student intake data if studentIntakeId is provided
  useEffect(() => {
    const loadSiblings = async () => {
      try {
        const response = await axios.get('/api/student-intake/my-intake');
        const allStudents = response.data;
        const siblingsList = allStudents.filter(student => student.id !== studentIntakeId);
        setSiblings(siblingsList);
      } catch (error) {
        console.error('Failed to fetch siblings:', error);
      }
    };

    if (studentIntakeId) {
      // Check if this student was already submitted (prevent showing empty form after refresh)
      if (sessionStorage.getItem(`student_submitted_${studentIntakeId}`) === 'true') {
        navigate('/parent/students');
        return;
      }

      // Clear existing form data when switching students
      setFormValues({});
      latestFormValuesRef.current = null;
      setHelperFormData({});
      setHouseholdInformation({});
      setParentHelperData({});
      setFieldsFromStudentIntake(new Set());
      setStudentIntake(null);
      setHasRunInitialAutofill(false);
      setManuallyChangedHelperFields(new Set());
      setHasUserStartedFilling(false);
      setLastSavedTime(null);
      setValidationErrors({});
      setError('');
      setSuccess(false);
      setLoading(true);
      fetchStudentIntake();
      loadSiblings();
      loadHouseholdInformation();
    } else {
      // Clear data for generic new student form
      setFormValues({});
      latestFormValuesRef.current = null;
      setHelperFormData({});
      setHouseholdInformation({});
      setParentHelperData({});
      setStudentIntake(null);
      setHasRunInitialAutofill(false);
      setManuallyChangedHelperFields(new Set());
      setHasUserStartedFilling(false);
      setLastSavedTime(null);
      setValidationErrors({});
      setError('');
      setSuccess(false);
      loadSiblings();
      loadHouseholdInformation();
    }
    siblingsAutofillRef.current = null;
  }, [studentIntakeId, navigate, fetchStudentIntake]);

  // Helper function to update form field values with helper data
  const updateHelperFormFieldValues = useCallback((currentHelperData) => {
    const updatedFormValues = { ...formValues };

    let helperInstanceFound = false;
    let valuesUpdated = 0;

    allFormInstances.forEach(instance => {
      if ((instance.instance_name || instance.master_form_name || '').includes('Helper Form')) {
        helperInstanceFound = true;
        Object.keys(currentHelperData).forEach(fieldName => {
          const prefixedFieldName = `${instance.id}_${fieldName}`;
          const newValue = currentHelperData[fieldName];
          if (newValue && newValue !== updatedFormValues[prefixedFieldName]) {
            updatedFormValues[prefixedFieldName] = newValue;
            valuesUpdated++;
          }
        });
      }
    });

    if (!helperInstanceFound) {
      console.warn('Helper form instance not found in allFormInstances!');
    }
    if (valuesUpdated > 0) {
      setFormValues(updatedFormValues);
    }
  }, [formValues, allFormInstances]);

  const loadSavedProgress = useCallback(async () => {
    try {
      const progressResponse = await axios.get(`/api/form-submissions/progress?student_intake_id=${studentIntakeId}`);


      const allFormData = progressResponse.data;

      // Find progress for the combined form (null form_instance_id)
      const combinedFormProgress = allFormData.find(progress =>
        progress.form_instance_id === null && !progress.is_complete
      );

      // Essential debug logging

      // Always initialize helper form data first
      let helperData = {};

      // Load parent-level helper data (only parent/family data, not student-specific)
      if (Object.keys(parentHelperData).length > 0) {
        helperData = { ...parentHelperData };
      }

      // For new enrollments (no saved progress), use household information to populate helper form
      const hasSavedProgress = !hasUserStartedFilling &&
                               !!combinedFormProgress &&
                               !!combinedFormProgress.submission_data &&
                               Object.keys(combinedFormProgress.submission_data).length > 0;

      if (!hasSavedProgress && Object.keys(householdInformation).length > 0) {
        // Use household information for new enrollments
        helperData = { ...householdInformation };

        // Map household phone fields to helper form expectations
        if (householdInformation.primary_parent_cell_phone) {
          helperData.primary_parent_cell_phone = householdInformation.primary_parent_cell_phone;
        }
        if (householdInformation.primary_parent_home_phone) {
          helperData.primary_parent_home_phone = householdInformation.primary_parent_home_phone;
        }
      }

      // Pre-populate with student intake data if available (for student-specific fields only)
      // This overrides parent data for student-specific fields
      if (studentIntake) {
        if (studentIntake.first_name) {
          helperData['legal_name_first'] = studentIntake.first_name;
        }
        if (studentIntake.last_name) {
          helperData['legal_name_last'] = studentIntake.last_name;
        }
        if (studentIntake.date_of_birth) {
          // Format the date to yyyy-MM-dd format for HTML date inputs
          let formattedDate = studentIntake.date_of_birth;
          if (studentIntake.date_of_birth.includes('T')) {
            formattedDate = studentIntake.date_of_birth.split('T')[0];
          }
          helperData['date_of_birth'] = formattedDate;
        }
      }


      // Load combined form progress data for new/incomplete submissions
      // hasSavedProgress was already calculated above

      // Simplified approach: Always initialize helper form with available data
      // If there's saved progress, extract helper data from it, otherwise use the data we prepared
      if (hasSavedProgress) {
        const savedData = combinedFormProgress.submission_data;

        // Extract helper form data from saved progress (overrides parent data)
        Object.keys(savedData).forEach(prefixedFieldName => {
          const parts = prefixedFieldName.split('_');
          if (parts.length >= 2) {
            const instanceId = parts[0];
            const originalFieldName = parts.slice(1).join('_');

            const instance = allFormInstances.find(inst => inst.id === instanceId);
            if (instance) {
              const instanceName = instance.instance_name || instance.master_form_name || '';
              if (instanceName.includes('Helper Form')) {
                const value = savedData[prefixedFieldName];
                if (value !== null && value !== undefined && value !== '') {
                  if (originalFieldName === 'primary_parent_phone') {
                    helperData.primary_parent_cell_phone = value;
                  } else {
                    helperData[originalFieldName] = value;
                  }
                }
              }
            }
          }
        });

        // Load saved data since user hasn't started filling yet
        setFormValues(savedData);
        previousFormValuesRef.current = savedData; // Initialize ref
        latestFormValuesRef.current = null; // Clear - we're loading, not from user input
      }

      // Always update helper form fields with whatever helperData we have
      updateHelperFormFieldValues(helperData);
      setHelperFormData(helperData);
      setHasLoadedProgress(true);
    } catch (error) {
      console.error('Failed to load saved progress:', error);
      // Not critical, continue without saved progress
    }
  }, [studentIntakeId, parentHelperData, householdInformation, allFormInstances, hasUserStartedFilling, studentIntake, updateHelperFormFieldValues]);

  // Calculate current grade (memoized to avoid unnecessary re-renders)
  const currentGrade = useMemo(() => {
    // Find the grade field - it should be prefixed with the new student form instance ID
    // Look for fields ending with '_grade' (the grade field from new student form)
    // Priority: look for exact match first, then check all grade fields

    // First, check for the helper form's grade field (exact name "grade")
    if (formValues['grade'] && (typeof formValues['grade'] === 'string' ? formValues['grade'].trim() !== '' : true)) {
      return formValues['grade'];
    }

    for (const key in formValues) {
      // Check for the main grade field (not previous_school_grade, etc.)
      if (key.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_grade$/i)) {
        const value = formValues[key];
        if (value && (typeof value === 'string' ? value.trim() !== '' : true)) {
          return value;
        }
      }
    }

    // Fallback: look for any _grade field that's not a previous/sibling/retained grade
    for (const key in formValues) {
      if (key.endsWith('_grade') && formValues[key] &&
          !key.includes('previous_school_grade') &&
          !key.includes('previous_enrollment_grade') &&
          !key.includes('retained_grade') &&
          !key.includes('sibling_grade')) {
        const value = formValues[key];
        if (value && (typeof value === 'string' ? value.trim() !== '' : true)) {
          return value;
        }
      }
    }
    return null;
  }, [formValues]);

  // Calculate student grade from birthdate
  const calculateStudentGrade = useCallback((student) => {
    if (!student || !student.date_of_birth) return null;

    const birthDate = new Date(student.date_of_birth);
    const today = new Date();
    const currentMonth = today.getMonth();
    const birthMonth = birthDate.getMonth();

    let age = today.getFullYear() - birthDate.getFullYear();

    // Adjust age if birthday hasn't occurred this year
    if (currentMonth < birthMonth || (currentMonth === birthMonth && today.getDate() < birthDate.getDate())) {
      age--;
    }


    // Determine grade based on age (rough approximation)
    // Kindergarten: ~5 years old
    // Grade 1: ~6 years old
    // Grade 2: ~7 years old
    // etc.
    if (age < 5) return 'Preschool';
    if (age === 5) return 'Kindergarten';
    if (age >= 6 && age <= 17) return (age - 5).toString(); // Grade = age - 5
    return '12'; // Max grade
  }, []);

  // Filter form instances based on grade
  const filterFormInstancesByGrade = useCallback((instances, grade) => {
    if (!instances || instances.length === 0) return instances;

    return instances.filter(instance => {
      const formName = (instance.instance_name || instance.master_form_name || '').toLowerCase();

      // Exclude returning student form
      if (formName.includes('returning')) {
        return false;
      }

      // Exclude school lunch application
      if (formName.includes('school lunch') || formName.includes('lunch')) {
        return false;
      }

      // Chromebook Agreement: Required for grades 1-12, NOT for Kindergarten/Preschool
      if (formName.includes('chromebook')) {
        if (!grade || grade === 'Kindergarten' || grade === 'Preschool') {
          return false; // Hide for Kindergarten/Preschool
        }
        // Show and mark as required for grades 1-12
        instance.is_required = true; // Mark as required for this student
        return true;
      }

      // Early Dismissal Permissions: Required for K-6, NOT for 7-12
      if (formName.includes('early dismissal') || formName.includes('dismissal')) {
        if (!grade) {
          return true; // Show if grade not selected yet
        }
        const numericGrade = parseInt(grade);
        if (isNaN(numericGrade)) {
          // Handle non-numeric grades (Kindergarten, Preschool)
          if (grade === 'Kindergarten' || grade === 'Preschool') {
            instance.is_required = true; // Mark as required
            return true; // Show for K/Preschool
          }
          return true; // Default to showing if can't parse
        }

        // Hide for grades 7-12
        if (numericGrade >= 7 && numericGrade <= 12) {
          return false;
        }

        // Show and mark as required for grades K-6
        instance.is_required = true; // Mark as required
        return true;
      }

      // Include all other forms
      return true;
    });
  }, []);

  const fetchFormInstances = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/form-instances/parent');

      if (response.data && response.data.length > 0) {
        // Store all instances
        setAllFormInstances(response.data);

        // Get current grade from student intake data (form values not available yet during initial load)
        const studentGrade = calculateStudentGrade(studentIntake);
        const currentGrade = studentGrade;

        // Exclude helper form when there are completed submissions

        // Get filtered instances
        const finalInstances = filterFormInstancesByGrade(response.data, currentGrade);

        if (finalInstances.length === 0) {
          setError('No new student forms found for your district');
          setLoading(false);
          return;
        }

        // Sort forms in the desired order for parents
        const desiredOrder = [
          'Helper Form',
          'Emergency Health form',
          'Residency verification',
          'Acceptable use agreement',
          'New Student form',
          'Chromebook agreement',
          'Early Dismissal Permissions'
        ];

        const sortedInstances = finalInstances.sort((a, b) => {
          const aName = a.instance_name || a.master_form_name || '';
          const bName = b.instance_name || b.master_form_name || '';

          // Find positions in desired order
          let aIndex = desiredOrder.length;
          let bIndex = desiredOrder.length;

          for (let i = 0; i < desiredOrder.length; i++) {
            if (aName.toLowerCase().includes(desiredOrder[i].toLowerCase()) ||
                desiredOrder[i].toLowerCase().includes(aName.toLowerCase())) {
              aIndex = i;
            }
            if (bName.toLowerCase().includes(desiredOrder[i].toLowerCase()) ||
                desiredOrder[i].toLowerCase().includes(bName.toLowerCase())) {
              bIndex = i;
            }
          }

          return aIndex - bIndex;
        });

        // Store current scroll position before updating to prevent page jumping
        const scrollY = window.scrollY;
        setScrollPosition(scrollY);

        setFormInstances(sortedInstances);

        // Combine all form instances into one
        const combined = combineFormInstances(sortedInstances);
        if (combined) {
          setCombinedFormDefinition(combined);
          setFormInstanceMap(combined.formInstanceMap);

          // Initialize form values with all possible field keys from the combined form
          const initializedFormValues = {};
          Object.keys(combined.formInstanceMap).forEach(fieldKey => {
            initializedFormValues[fieldKey] = '';
          });

          // Pre-populate with student intake data if available
          if (studentIntake) {
            Object.keys(initializedFormValues).forEach(fieldKey => {
              const parts = fieldKey.split('_');
              if (parts.length >= 2) {
                const originalFieldName = parts.slice(1).join('_');

                // Populate common fields from student intake
                if (originalFieldName === 'legal_name_first' && studentIntake.first_name) {
                  initializedFormValues[fieldKey] = studentIntake.first_name;
                } else if (originalFieldName === 'legal_name_last' && studentIntake.last_name) {
                  initializedFormValues[fieldKey] = studentIntake.last_name;
                } else if (originalFieldName === 'date_of_birth' && studentIntake.date_of_birth) {
                  let formattedDate = studentIntake.date_of_birth;
                  if (studentIntake.date_of_birth.includes('T')) {
                    formattedDate = studentIntake.date_of_birth.split('T')[0];
                  }
                  initializedFormValues[fieldKey] = formattedDate;
                }
              }
            });
          }

          setFormValues(initializedFormValues);
          previousFormValuesRef.current = initializedFormValues;

          // Initialize autofill enabled state for forms that get autofilled
          const initialAutofillEnabled = {};
          sortedInstances.forEach(instance => {
            if (isFormAutofilled(instance)) {
              initialAutofillEnabled[instance.id] = true; // Default to enabled
            }
          });
          setAutofillEnabled(initialAutofillEnabled);
        } else {
          setError('Failed to combine form instances');
        }
      } else {
        setError('No active forms found for your district');
      }
    } catch (error) {
      console.error('Failed to fetch form instances:', error);
      setError('Failed to load forms. Please try again later.');
    } finally {
      setLoading(false);
      // Load saved progress after forms are loaded
      loadSavedProgress();
    }
  }, [studentIntake, filterFormInstancesByGrade]); // Re-enabled safe dependencies

  // Load saved progress when student intake data becomes available
  // Load saved progress when student intake data is available
  useEffect(() => {
    if (studentIntakeId && studentIntake && combinedFormDefinition) {
      loadSavedProgress();
    }
  }, [studentIntakeId, studentIntake, combinedFormDefinition]); // loadSavedProgress dependency removed to avoid init issues


  // Extract helper form data from completed submission

  const calculateProgress = useCallback(() => {
    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return 0;
    }

    let totalFields = 0;
    let filledFields = 0;

    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;
      
      section.fields.forEach(field => {
        if (field.admin_only) return;
        
        // Check visibility
        let isVisible = true;
        if (field.conditional) {
          const conditionalField = field.conditional.field;
          const conditionalValue = field.conditional.value;
          const currentValue = formValues[conditionalField];
          isVisible = Array.isArray(conditionalValue) 
            ? conditionalValue.includes(currentValue)
            : conditionalValue === currentValue;
        }

        if (!isVisible) return;

        totalFields++;
        const value = formValues[field.name];
        
        if (field.type === 'checkbox') {
          if (value && Array.isArray(value) && value.length > 0) {
            filledFields++;
          }
        } else if (value && (typeof value === 'string' && value.trim() !== '')) {
          filledFields++;
        }
      });
    });

    return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  }, [combinedFormDefinition, formValues]);

  const saveProgress = useCallback(async () => {
    if (savingProgress) return; // Prevent multiple simultaneous saves

    try {
      setSavingProgress(true);
      const progressPercentage = calculateProgress();

      await axios.post('/api/form-submissions/save-progress', {
        submission_data: formValues,
        progress_percentage: progressPercentage,
        form_instance_id: null, // Combined form has null form_instance_id
        student_intake_id: studentIntakeId // Link to specific student
      });
      setLastSavedTime(new Date());

      // Notify parent dashboard about progress update
      window.dispatchEvent(new CustomEvent('progressUpdated'));

    } catch (error) {
      console.error('Failed to save progress:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Don't show error to user for auto-save failures
    } finally {
      setSavingProgress(false);
    }
  }, [formValues, savingProgress, calculateProgress, studentIntakeId]);

  useEffect(() => {
    fetchFormInstances();
  }, [studentIntakeId]); // fetchFormInstances dependency removed to avoid init issues


  // Re-filter form instances when grade changes or allFormInstances changes
  useEffect(() => {
    if (allFormInstances.length === 0) return;

    // For dynamic filtering, exclude helper form when there are completed submissions
    const filteredInstances = filterFormInstancesByGrade(allFormInstances, currentGrade);


    // Sort forms in the desired order for parents
    const desiredOrder = [
      'Helper Form',
      'Emergency Health form',
      'Residency verification',
      'Acceptable use agreement',
      'New Student form',
      'Chromebook agreement',
      'Early Dismissal Permissions'
    ];

    const sortedInstances = filteredInstances.sort((a, b) => {
      const aName = a.instance_name || a.master_form_name || '';
      const bName = b.instance_name || b.master_form_name || '';

      // Find positions in desired order
      let aIndex = desiredOrder.length;
      let bIndex = desiredOrder.length;

      for (let i = 0; i < desiredOrder.length; i++) {
        if (aName.toLowerCase().includes(desiredOrder[i].toLowerCase()) ||
            desiredOrder[i].toLowerCase().includes(aName.toLowerCase())) {
          aIndex = i;
        }
        if (bName.toLowerCase().includes(desiredOrder[i].toLowerCase()) ||
            desiredOrder[i].toLowerCase().includes(bName.toLowerCase())) {
          bIndex = i;
        }
      }

      return aIndex - bIndex;
    });

    // Only update if the filtered list changed
    const currentIds = formInstances.map(i => i.id).sort().join(',');
    const newIds = sortedInstances.map(i => i.id).sort().join(',');

    if (currentIds !== newIds) {
      // Store current scroll position before updating to prevent page jumping
      const scrollY = window.scrollY;
      setScrollPosition(scrollY);

      setFormInstances(sortedInstances);

      // Recombine form instances
      const combined = combineFormInstances(sortedInstances);
      if (combined) {
        setCombinedFormDefinition(combined);
        setFormInstanceMap(combined.formInstanceMap);

        // Initialize form values with all possible field keys from the combined form
        // Preserve existing values, especially grade fields which control visibility
        const initializedFormValues = { ...formValues };
        Object.keys(combined.formInstanceMap).forEach(fieldKey => {
          if (!(fieldKey in initializedFormValues)) {
            initializedFormValues[fieldKey] = '';
          }
        });
        // Don't overwrite grade fields - they're critical for form filtering
        // Find all grade fields (including prefixed ones like {instanceId}_grade)
        const gradeFields = Object.keys(formValues).filter(key => {
          const isUnprefixedGrade = key === 'grade';
          const isPrefixedGrade = key.endsWith('_grade') && 
            !key.includes('previous_school_grade') &&
            !key.includes('previous_enrollment_grade') && 
            !key.includes('retained_grade') &&
            !key.includes('sibling_grade');
          return isUnprefixedGrade || isPrefixedGrade;
        });
        
        // Preserve grade field values - check both old and new field keys
        gradeFields.forEach(gradeKey => {
          const oldValue = formValues[gradeKey];
          if (oldValue !== undefined && oldValue !== null && oldValue !== '') {
            initializedFormValues[gradeKey] = oldValue;
            Object.keys(combined.formInstanceMap).forEach(newFieldKey => {
              const newFieldName = newFieldKey.split('_').slice(1).join('_');
              const oldFieldName = gradeKey.split('_').slice(1).join('_');
              if (newFieldName === 'grade' && oldFieldName === 'grade' && newFieldKey !== gradeKey) {
                initializedFormValues[newFieldKey] = oldValue;
              }
            });
          }
        });

        // CRITICAL: Preserve ALL helper form fields (first name, last name, age, etc.) when grade changes
        // Use both formValues and previousFormValuesRef - ref may have latest from handleFormChange before state commits
        const helperInstance = sortedInstances.find(inst =>
          (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
        );
        if (helperInstance) {
          const sources = [formValues, previousFormValuesRef.current].filter(Boolean);
          sources.forEach(source => {
            Object.keys(source).forEach(key => {
              if (key.startsWith(`${helperInstance.id}_`)) {
                const val = source[key];
                if (val !== null && val !== undefined && val !== '' && val !== 'Please select an option' &&
                    !(Array.isArray(val) && val.length === 0)) {
                  initializedFormValues[key] = val;
                }
              }
            });
          });
        }

        setFormValues(initializedFormValues);
        previousFormValuesRef.current = initializedFormValues;

        // Initialize autofill enabled state for forms that get autofilled
        const initialAutofillEnabled = {};
        sortedInstances.forEach(instance => {
          if (isFormAutofilled(instance)) {
            initialAutofillEnabled[instance.id] = true; // Default to enabled
          }
        });
        setAutofillEnabled(initialAutofillEnabled);

        // Remove form data for forms that are no longer visible
        // But preserve grade fields since they affect form filtering
        const cleanedFormValues = {};
        const allKeys = new Set([...Object.keys(formValues), ...Object.keys(previousFormValuesRef.current || {})]);
        allKeys.forEach(key => {
          const belongsToVisible = filteredInstances.some(inst => key.startsWith(`${inst.id}_`));
          const isGradeField = key === 'grade' || key.endsWith('_grade') ||
            (key.includes('grade') && !key.includes('previous_school_grade') &&
            !key.includes('previous_enrollment_grade') && !key.includes('retained_grade') &&
            !key.includes('sibling_grade'));

          if (belongsToVisible || isGradeField) {
            const val = formValues[key] ?? previousFormValuesRef.current?.[key];
            if (val !== undefined) cleanedFormValues[key] = val;
          }
        });

        // Only update if values changed
        if (JSON.stringify(cleanedFormValues) !== JSON.stringify(formValues)) {
          setFormValues(cleanedFormValues);
          previousFormValuesRef.current = cleanedFormValues;
        }
      }
    }
  }, [allFormInstances, filterFormInstancesByGrade, currentGrade]);

  // Autofill fields from helper form data

  const applyHelperFormAutofill = useCallback((currentValues, helperData, allHelperData) => {
    if (!helperData || Object.keys(helperData).length === 0) {
      return currentValues;
    }

    const updatedValues = { ...currentValues };

    // Check if helper form is actually included in the current form combination
    // If not, skip autofill entirely to prevent overwriting existing data
    const helperFormIncluded = combinedFormDefinition?.sections?.some(section =>
      section.form_instance_id && allFormInstances?.find(inst =>
        inst.id === section.form_instance_id &&
        (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
      )
    );


    if (!helperFormIncluded) {
      return currentValues;
    }

    // Treat placeholder as empty - never overwrite valid selections with "Please select an option"
    const isEmptyOrPlaceholder = (v) =>
      v === null || v === undefined || v === '' || v === 'Please select an option';

    // Apply helper form data to matching fields in other forms
    Object.keys(helperData).forEach(helperFieldName => {
      const helperValue = helperData[helperFieldName];

      // Skip if helper field is empty or placeholder (fixes ~25% grade revert)
      if (isEmptyOrPlaceholder(helperValue)) {
        return;
      }

      // Find fields in other forms that match this helper field name
      Object.keys(updatedValues).forEach(prefixedFieldName => {
        // Extract the original field name from the prefixed field name
        const parts = prefixedFieldName.split('_');
        if (parts.length >= 2) {
          const instanceId = parts[0];
          const originalFieldName = parts.slice(1).join('_'); // Everything after first underscore

          // If the original field name matches the helper field name, autofill it
          if (originalFieldName === helperFieldName) {
            // Skip setting values for helper form fields, as they are the source
            const instance = allFormInstances.find(inst => inst.id === instanceId);
            if (instance && (instance.instance_name || instance.master_form_name || '').toLowerCase().includes('helper')) {
              // Don't overwrite helper form fields
              return;
            }


            // Check if autofill is enabled for this form instance
            const autofillAllowed = autofillEnabled[instanceId] !== false; // Default to true if not set

            if (autofillAllowed) {
              // Autofill can overwrite existing values when enabled
              updatedValues[prefixedFieldName] = helperValue;
            } else {
            }
          }

          // Special cross-form autofill mappings
          // Note: student_name autofill is handled separately after this loop to ensure both first and last names are available
          // This prevents partial autofill when only one name is set
          if (helperFieldName === 'date_of_birth' && originalFieldName === 'student_birth_date') {
            // Map date_of_birth to student_birth_date
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            }
          }
          if (helperFieldName === 'form_date' && originalFieldName === 'form_date') {
            // Map form_date to form_date (same field name, but ensure it works across forms)
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            }
          }
          if (helperFieldName === 'primary_parent_last_name' && originalFieldName === 'parent_last_name') {
            // Map primary parent last name to acceptable use agreement parent last name
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            } else {
            }
          }
          if (helperFieldName === 'primary_parent_first_name' && originalFieldName === 'parent_first_name') {
            // Map primary parent first name to acceptable use agreement parent first name
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            } else {
            }
          }
          if (helperFieldName === 'home_city' && originalFieldName === 'primary_address_city') {
            // Map home city to primary parent household city
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            }
          }
          if (helperFieldName === 'home_state' && originalFieldName === 'primary_address_state') {
            // Map home state to primary parent household state
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            }
          }
          if (helperFieldName === 'home_zip_code' && originalFieldName === 'primary_address_zip') {
            // Map home zip to primary parent household zip
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            }
          }
          if (helperFieldName === 'primary_parent_cell_phone' && originalFieldName === 'primary_parent_cell_phone') {
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            }
          }
          if (helperFieldName === 'primary_parent_home_phone' && (originalFieldName === 'primary_home_phone' || originalFieldName === 'primary_parent_home_phone')) {
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            }
          }
          if (helperFieldName === 'home_street_address' && originalFieldName === 'primary_address_street') {
            // Map home street address to primary address street
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            } else {
            }
          }
          if (helperFieldName === 'home_city' && originalFieldName === 'primary_address_city') {
            // Map home city to primary address city
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            } else {
            }
          }
          if (helperFieldName === 'home_zip_code' && originalFieldName === 'primary_address_zip') {
            // Map home zip to primary address zip
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            } else {
            }
          }
          if (helperFieldName === 'home_county' && originalFieldName === 'home_county') {
            // Map home county to home county (same field name)
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            } else {
            }
          }
          if (helperFieldName === 'home_county' && originalFieldName === 'primary_address_county') {
            // Map helper county value into the New Student Primary Address County field
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            } else {
            }
          }
          if (helperFieldName === 'home_state' && originalFieldName === 'home_state') {
            // Map home state to home state (same field name)
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) {
              updatedValues[prefixedFieldName] = helperValue;
            } else {
            }
          }
          if (helperFieldName === 'student_age' && (originalFieldName === 'student_age' || originalFieldName === 'age')) {
            // CRITICAL: Never overwrite the helper form's own student_age field - it's the source!
            // Skip if this is the helper form itself
            const instance = allFormInstances.find(inst => inst.id === instanceId);
            if (instance && (instance.instance_name || instance.master_form_name || '').toLowerCase().includes('helper')) {
              // This is the helper form - don't overwrite its own student_age field
              return;
            }
            
            // Map student age to student_age or age fields in OTHER forms
            // BUT: Don't overwrite if user has manually entered a value (prevent autofill from overwriting manual entries)
            const currentValue = updatedValues[prefixedFieldName];
            const hasManualEntry = currentValue !== null && currentValue !== undefined && currentValue !== '';
            
            // Only autofill if field is empty or autofill is explicitly enabled
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed && !hasManualEntry) {
              updatedValues[prefixedFieldName] = helperValue;
            } else {
              // Preserve manual entry - don't overwrite
            }
          }
          if (helperFieldName === 'primary_parent_cell_phone' && originalFieldName === 'parent_guardian_phone') {
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed) updatedValues[prefixedFieldName] = helperValue;
          }
          if (helperFieldName === 'primary_parent_home_phone' && originalFieldName === 'parent_guardian_phone') {
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            if (autofillAllowed && !updatedValues[prefixedFieldName]) {
              updatedValues[prefixedFieldName] = helperValue;
            }
          }
        }
      });
    });

    // Autofill student_name fields from legal_name_first and legal_name_last
    // This runs after the helperData loop to ensure all student_name fields are checked
    // IMPORTANT: Only autofill if BOTH first and last names are available to prevent partial names
    const firstName = allHelperData.legal_name_first;
    const lastName = allHelperData.legal_name_last;
    
    // Check if both names are available and non-empty
    const hasBothNames = firstName && lastName && firstName.trim() !== '' && lastName.trim() !== '';
    
    if (hasBothNames) {
      Object.keys(updatedValues).forEach(prefixedFieldName => {
        const parts = prefixedFieldName.split('_');
        if (parts.length >= 2) {
          const instanceId = parts[0];
          const originalFieldName = parts.slice(1).join('_');
          
          if (originalFieldName === 'student_name') {
            const autofillAllowed = autofillEnabled[instanceId] !== false;
            const currentValue = updatedValues[prefixedFieldName];
            const hasManualEntry = currentValue !== null && currentValue !== undefined && currentValue !== '';
            
            if (autofillAllowed && !hasManualEntry) {
              // Only set if we have both names - clear if only partial name exists
              updatedValues[prefixedFieldName] = `${firstName.trim()} ${lastName.trim()}`;
            }
          }
        }
      });
    } else {
      // If we don't have both names, clear any partial student_name entries that might have been set incorrectly
      Object.keys(updatedValues).forEach(prefixedFieldName => {
        const parts = prefixedFieldName.split('_');
        if (parts.length >= 2) {
          const originalFieldName = parts.slice(1).join('_');
          
          if (originalFieldName === 'student_name') {
            const currentValue = updatedValues[prefixedFieldName];
            // If current value matches only first name (without last name), clear it
            if (currentValue && firstName && currentValue.trim() === firstName.trim() && !lastName) {
              updatedValues[prefixedFieldName] = '';
            }
          }
        }
      });
    }

    // Handle "same as primary parent" checkboxes for mother/guardian and father/guardian
    const motherSameAsPrimary = allHelperData.mother_same_as_primary;
    const fatherSameAsPrimary = allHelperData.father_same_as_primary;

    if (motherSameAsPrimary) {
      const primaryFirstName = allHelperData.primary_parent_first_name;
      const primaryLastName = allHelperData.primary_parent_last_name;
      const primaryCellPhone = allHelperData.primary_parent_cell_phone;
      const primaryHomePhone = allHelperData.primary_parent_home_phone;

      Object.keys(updatedValues).forEach(prefixedFieldName => {
        const parts = prefixedFieldName.split('_');
        if (parts.length >= 2) {
          const instanceId = parts[0];
          const originalFieldName = parts.slice(1).join('_');
          const autofillAllowed = autofillEnabled[instanceId] !== false;

          if (originalFieldName === 'mother_guardian_name' && primaryFirstName && primaryLastName) {
            if (autofillAllowed) updatedValues[prefixedFieldName] = `${primaryFirstName} ${primaryLastName}`;
          }
          if (originalFieldName === 'mother_home_phone' && primaryHomePhone) {
            if (autofillAllowed) updatedValues[prefixedFieldName] = primaryHomePhone;
          }
          if (originalFieldName === 'mother_cell_phone' && primaryCellPhone) {
            if (autofillAllowed) updatedValues[prefixedFieldName] = primaryCellPhone;
          }
        }
      });
    }

    if (fatherSameAsPrimary) {
      const primaryFirstName = allHelperData.primary_parent_first_name;
      const primaryLastName = allHelperData.primary_parent_last_name;
      const primaryCellPhone = allHelperData.primary_parent_cell_phone;
      const primaryHomePhone = allHelperData.primary_parent_home_phone;

      Object.keys(updatedValues).forEach(prefixedFieldName => {
        const parts = prefixedFieldName.split('_');
        if (parts.length >= 2) {
          const instanceId = parts[0];
          const originalFieldName = parts.slice(1).join('_');
          const autofillAllowed = autofillEnabled[instanceId] !== false;

          if (originalFieldName === 'father_guardian_name' && primaryFirstName && primaryLastName) {
            if (autofillAllowed) updatedValues[prefixedFieldName] = `${primaryFirstName} ${primaryLastName}`;
          }
          if (originalFieldName === 'father_home_phone' && primaryHomePhone) {
            if (autofillAllowed) updatedValues[prefixedFieldName] = primaryHomePhone;
          }
          if (originalFieldName === 'father_cell_phone' && primaryCellPhone) {
            if (autofillAllowed) updatedValues[prefixedFieldName] = primaryCellPhone;
          }
        }
      });
    }

    // Special handling for Authorized Pick-Up Persons section
    // Auto-populate first entry with primary parent information
    if (helperData.primary_parent_first_name && helperData.primary_parent_last_name) {
      // Find the early dismissal form instance
      const earlyDismissalInstance = allFormInstances?.find(inst =>
        (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('early') &&
        (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('dismissal')
      );

      if (earlyDismissalInstance) {
        const instanceId = earlyDismissalInstance.id;

        // Populate authorized person fields (first entry)
        const authorizedLastNameKey = `${instanceId}_authorized_person_last_name`;
        const authorizedFirstNameKey = `${instanceId}_authorized_person_first_name`;

        // Only populate if these fields exist and are empty
        if (updatedValues[authorizedLastNameKey] === '' || updatedValues[authorizedLastNameKey] === undefined) {
          updatedValues[authorizedLastNameKey] = helperData.primary_parent_last_name;
        }
        if (updatedValues[authorizedFirstNameKey] === '' || updatedValues[authorizedFirstNameKey] === undefined) {
          updatedValues[authorizedFirstNameKey] = helperData.primary_parent_first_name;
        }
      }
    }

    // Special handling for siblings sections
    // Auto-populate sibling information from other students in the family
    if (siblings && siblings.length > 0) {
      // Find siblings sections in forms
      Object.keys(updatedValues).forEach(prefixedFieldName => {
        const parts = prefixedFieldName.split('_');
        if (parts.length >= 2) {
          const instanceId = parts[0];
          const originalFieldName = parts.slice(1).join('_');

          // Check if this is a sibling-related field
          if (originalFieldName === 'sibling_name') {
            const instance = allFormInstances?.find(inst => inst.id === instanceId);
            if (instance && siblings.length > 0) {
              // Populate sibling information
              siblings.forEach((sibling, index) => {
                const siblingNameKey = `${instanceId}_sibling_name_${index}`;
                const siblingGradeKey = `${instanceId}_sibling_grade_${index}`;

                // Only populate if the fields exist and are empty
                if (updatedValues[siblingNameKey] === '' || updatedValues[siblingNameKey] === undefined) {
                  updatedValues[siblingNameKey] = `${sibling.first_name} ${sibling.last_name}`;
                }
                if (updatedValues[siblingGradeKey] === '' || updatedValues[siblingGradeKey] === undefined) {
                  updatedValues[siblingGradeKey] = sibling.grade || '';
                }
              });
            }
          }
        }
      });
    }

    return updatedValues;
  }, [helperFormData]);

  const autofillSiblingRepeatGroups = useCallback((currentValues) => {
    if (!combinedFormDefinition?.sections || siblings.length === 0) {
      return currentValues;
    }

    let hasChanges = false;
    const updatedValues = { ...currentValues };

    combinedFormDefinition.sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.type !== 'repeatable_group') return;

        const hasSiblingFields = field.fields?.some(subField =>
          subField.name.toLowerCase().includes('sibling')
        );

        if (!hasSiblingFields) return;

        const existingEntries = currentValues[field.name];
        if (Array.isArray(existingEntries) && existingEntries.length > 0) {
          return;
        }

        const entries = siblings.map((sibling) => {
          const entry = {};
          field.fields.forEach(subField => {
            if (subField.name === 'sibling_name') {
              const firstName = sibling.first_name || sibling.legal_name_first || '';
              const lastName = sibling.last_name || sibling.legal_name_last || '';
              entry.sibling_name = `${firstName} ${lastName}`.trim();
            }
            if (subField.name === 'sibling_grade') {
              entry.sibling_grade = sibling.grade || sibling.grade_level || '';
            }
            if (subField.name === 'sibling_building') {
              entry.sibling_building = sibling.school_name || sibling.school || '';
            }
          });
          return entry;
        }).filter(entry => entry.sibling_name);

        if (entries.length > 0) {
          updatedValues[field.name] = entries;
          hasChanges = true;
        }
      });
    });

    return hasChanges ? updatedValues : currentValues;
  }, [combinedFormDefinition, siblings]);

  const handleAddFromStudentList = useCallback(() => {
    if (!combinedFormDefinition || siblings.length === 0) {
      setSiblingAutofillMessage('No students available to add');
      return;
    }

    const updatedValues = { ...formValues };
    let addedCount = 0;

    combinedFormDefinition.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (field.type !== 'repeatable_group') return;
        if (!field.fields?.some(subField => subField.name.toLowerCase().includes('sibling'))) return;

        const fieldName = field.name;
        const existingEntries = Array.isArray(formValues[fieldName]) ? [...formValues[fieldName]] : [];
        const existingNames = new Set(
          existingEntries
            .map(entry => (entry?.sibling_name || '').trim().toLowerCase())
            .filter(Boolean)
        );

        const newEntries = [];
        siblings.forEach(sibling => {
          const firstName = sibling.first_name || sibling.legal_name_first || '';
          const lastName = sibling.last_name || sibling.legal_name_last || '';
          const fullName = `${firstName} ${lastName}`.trim();
          if (!fullName) return;
          if (existingNames.has(fullName.toLowerCase())) return;
          existingNames.add(fullName.toLowerCase());

          const entry = {};
          field.fields.forEach(subField => {
            if (subField.name === 'sibling_name') {
              entry.sibling_name = fullName;
            }
            if (subField.name === 'sibling_grade') {
              entry.sibling_grade = sibling.grade || sibling.grade_level || '';
            }
            if (subField.name === 'sibling_building') {
              entry.sibling_building = sibling.school_name || sibling.school || '';
            }
          });

          if (Object.keys(entry).length > 0) {
            newEntries.push(entry);
          }
        });

        if (newEntries.length > 0) {
          updatedValues[fieldName] = [...existingEntries, ...newEntries];
          addedCount += newEntries.length;
        }
      });
    });

    if (addedCount > 0) {
      setFormValues(updatedValues);
      setSiblingAutofillMessage(`Added ${addedCount} sibling${addedCount > 1 ? 's' : ''} from student list.`);
    } else {
      setSiblingAutofillMessage('No new students to add');
    }
  }, [combinedFormDefinition, siblings, formValues]);

  useEffect(() => {
    if (
      !combinedFormDefinition ||
      siblings.length === 0 ||
      hasLoadedProgress ||
      hasUserStartedFilling ||
      siblingsAutofillRef.current === studentIntakeId
    ) {
      return;
    }

    const updatedValues = autofillSiblingRepeatGroups(formValues);
    if (updatedValues !== formValues) {
      setFormValues(updatedValues);
    }

    siblingsAutofillRef.current = studentIntakeId;
  }, [
    combinedFormDefinition,
    siblings,
    hasLoadedProgress,
    hasUserStartedFilling,
    studentIntakeId,
    formValues,
    autofillSiblingRepeatGroups
  ]);

  // Apply autofill when helper form data changes OR when forms first load
  useEffect(() => {

    // For in-progress forms (saved but not submitted), allow autofill to update with new helper data
    // Only skip autofill if this is an already submitted form being edited (which doesn't happen in NewStudentForm)

    // Only run autofill if we have helper data AND forms are loaded
    // For in-progress forms, allow autofill even if progress was loaded
    // Don't run on every formValues change (user input) - only on helper data changes or initial load
    if (Object.keys(helperFormData).length > 0 && combinedFormDefinition) {
      // CRITICAL: Use latestFormValuesRef when available - captures user changes before state commits
      // This fixes race where useEffect runs before setFormValues has committed
      const valuesToUse = (latestFormValuesRef.current && Object.keys(latestFormValuesRef.current).length > 0)
        ? latestFormValuesRef.current
        : formValues;

      // CRITICAL: Preserve ALL helper form field values from current form state before autofill
      // The helper form is the SOURCE - never let autofill overwrite it (fixes grade clearing when selecting gender/ethnicity)
      const helperFormInstance = allFormInstances.find(inst => 
        (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
      );
      
      const preservedHelperFields = {};
      if (helperFormInstance) {
        // Preserve ALL non-empty helper form fields from current form state
        Object.keys(valuesToUse).forEach(key => {
          if (key.startsWith(`${helperFormInstance.id}_`)) {
            const currentValue = valuesToUse[key];
            const isEmpty = currentValue === null || currentValue === undefined || currentValue === '' ||
              (typeof currentValue === 'string' && currentValue === 'Please select an option') ||
              (Array.isArray(currentValue) && currentValue.length === 0);
            if (!isEmpty) {
              preservedHelperFields[key] = currentValue;
            }
          }
        });
      }
      
      const autofilledValues = applyHelperFormAutofill(valuesToUse, helperFormData, helperFormData);
      
      // CRITICAL: Restore ALL helper form field values - never let autofill overwrite the source
      Object.keys(preservedHelperFields).forEach(fieldKey => {
        autofilledValues[fieldKey] = preservedHelperFields[fieldKey];
      });

      if (JSON.stringify(autofilledValues) !== JSON.stringify(valuesToUse)) {
        setFormValues(autofilledValues);
      }
      // Clear the ref after use so we don't use stale data on subsequent runs
      latestFormValuesRef.current = null;
    } else {
    }
  // NOTE: formValues intentionally excluded from deps - including it causes a loop:
  // setFormValues triggers re-run, which can overwrite user's grade, causing vacillation
  }, [helperFormData, applyHelperFormAutofill, combinedFormDefinition, hasLoadedProgress, allFormInstances]);

  // Auto-populate Authorized Pick-Up Persons with parent and emergency contact names
  useEffect(() => {
    if (!combinedFormDefinition || !formInstances.length || Object.keys(helperFormData).length === 0) {
      return;
    }

    // Find Early Dismissal Permissions form instance
    const earlyDismissalInstance = formInstances.find(inst => 
      (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('early dismissal')
    );

    if (!earlyDismissalInstance) {
      return;
    }

    const instanceId = earlyDismissalInstance.id;
    // Authorized Pick-Up Persons is section index 4 (0-indexed: Form Info=0, Student Info=1, Parent Contact=2, Dismissal Permissions=3, Authorized Pick-Up=4)
    const authorizedPersonsFieldName = `${instanceId}_repeating_group_4`;

    // Get parent and emergency contact names from helper form data
    const personsToAdd = [];
    
    // Primary parent
    if (helperFormData.primary_parent_first_name && helperFormData.primary_parent_last_name) {
      const fullName = `${helperFormData.primary_parent_first_name} ${helperFormData.primary_parent_last_name}`;
      if (!deletedAuthorizedPersons.has(fullName)) {
        personsToAdd.push({
          authorized_person_first_name: helperFormData.primary_parent_first_name,
          authorized_person_last_name: helperFormData.primary_parent_last_name
        });
      }
    }

    // Alternate parent
    if (helperFormData.alternate_parent_first_name && helperFormData.alternate_parent_last_name) {
      const fullName = `${helperFormData.alternate_parent_first_name} ${helperFormData.alternate_parent_last_name}`;
      if (!deletedAuthorizedPersons.has(fullName)) {
        personsToAdd.push({
          authorized_person_first_name: helperFormData.alternate_parent_first_name,
          authorized_person_last_name: helperFormData.alternate_parent_last_name
        });
      }
    }

    // Emergency contacts (check for multiple emergency contacts)
    const emergencyContactKeys = Object.keys(helperFormData).filter(key => 
      key.startsWith('emergency_contact') && (key.includes('first_name') || key.includes('last_name'))
    );

    // Group emergency contacts by index
    const emergencyContactIndices = new Set();
    emergencyContactKeys.forEach(key => {
      const match = key.match(/emergency_contact_(\d+)_(first_name|last_name)/);
      if (match) {
        emergencyContactIndices.add(match[1]);
      } else if (key === 'emergency_contact_first_name' || key === 'emergency_contact_last_name') {
        emergencyContactIndices.add('0'); // Default index for non-indexed fields
      }
    });

    emergencyContactIndices.forEach(index => {
      const firstNameKey = index === '0' ? 'emergency_contact_first_name' : `emergency_contact_${index}_first_name`;
      const lastNameKey = index === '0' ? 'emergency_contact_last_name' : `emergency_contact_${index}_last_name`;
      
      const firstName = helperFormData[firstNameKey];
      const lastName = helperFormData[lastNameKey];
      
      if (firstName && lastName) {
        const fullName = `${firstName} ${lastName}`;
        if (!deletedAuthorizedPersons.has(fullName)) {
          personsToAdd.push({
            authorized_person_first_name: firstName,
            authorized_person_last_name: lastName
          });
        }
      }
    });

    if (personsToAdd.length === 0) {
      return;
    }

    // Get current authorized persons from form values (repeatable group is stored as array)
    const currentAuthorizedPersons = Array.isArray(formValues[authorizedPersonsFieldName]) 
      ? formValues[authorizedPersonsFieldName] 
      : [];
    const existingNames = new Set(
      currentAuthorizedPersons.map(person => 
        `${person.authorized_person_first_name || ''} ${person.authorized_person_last_name || ''}`.trim()
      )
    );

    // Only add persons that aren't already in the list
    const newPersons = personsToAdd.filter(person => {
      const fullName = `${person.authorized_person_first_name} ${person.authorized_person_last_name}`;
      return !existingNames.has(fullName);
    });

    if (newPersons.length > 0) {
      setFormValues(prev => ({
        ...prev,
        [authorizedPersonsFieldName]: [...currentAuthorizedPersons, ...newPersons]
      }));
    }
  }, [helperFormData, combinedFormDefinition, formInstances, deletedAuthorizedPersons, formValues]);

  // Run autofill once after progress is loaded and forms are ready
  // TEMPORARILY DISABLED for editing existing submissions
  useEffect(() => {
    if (hasLoadedProgress && combinedFormDefinition && !hasRunInitialAutofill) {
      // Temporarily disable autofill for existing submissions
      // if (Object.keys(helperFormData).length > 0) {
      //   const autofilledValues = applyHelperFormAutofill(formValues, helperFormData, helperFormData);
      //   if (JSON.stringify(autofilledValues) !== JSON.stringify(formValues)) {
      //     setFormValues(autofilledValues);
      //   }
      // }
      setHasRunInitialAutofill(true);
    }
  }, [hasLoadedProgress, combinedFormDefinition, hasRunInitialAutofill]); // Removed helperFormData, formValues, applyHelperFormAutofill dependencies

  // Restore scroll position after form updates to prevent page jumping
  useEffect(() => {
    if (scrollPosition > 0) {
      // Use setTimeout to ensure the DOM has updated before scrolling
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
        setScrollPosition(0); // Reset after restoring
      }, 0);
    }
  }, [combinedFormDefinition, scrollPosition]);

  // Auto-save progress every 30 seconds (disabled for editing completed submissions)
  useEffect(() => {
    if (Object.keys(formValues).length === 0) return;

    const autoSaveTimer = setTimeout(() => {
      saveProgress();
    }, 30000); // 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [formValues, saveProgress]);


  const validateForm = () => {
    const errors = {};
    
    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return errors;
    }

    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;
      
      section.fields.forEach(field => {
        if (field.admin_only) return;
        
        // Check visibility
        let isVisible = true;
        if (field.conditional) {
          const conditionalField = field.conditional.field;
          const conditionalValue = field.conditional.value;
          // The conditional field should already be prefixed by combineFormInstances
          // But check both prefixed and unprefixed versions for safety
          let currentValue = formValues[conditionalField];
          if (currentValue === undefined && field.form_instance_id) {
            // Try with instance ID prefix if not found
            const prefixedConditionalField = `${field.form_instance_id}_${conditionalField}`;
            currentValue = formValues[prefixedConditionalField];
          }
          isVisible = Array.isArray(conditionalValue)
            ? conditionalValue.includes(currentValue)
            : conditionalValue === currentValue;
        }

        if (!isVisible) return;

        // Check required
        const isRequired = field.required || 
          (field.conditional_required && isVisible);
        
        if (isRequired) {
          const value = formValues[field.name];
          
          // Handle different field types
          if (field.type === 'checkbox') {
            if (!value || !Array.isArray(value) || value.length === 0) {
              errors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'signature') {
            // Signature fields can be string (old format) or object with image property
            if (!value || 
                (typeof value === 'string' && value.trim() === '') ||
                (typeof value === 'object' && !value.image)) {
              errors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'date') {
            if (!value || value.trim() === '') {
              errors[field.name] = `${field.label} is required`;
            }
          } else if (!value || (typeof value === 'string' && value.trim() === '')) {
            errors[field.name] = `${field.label} is required`;
          }
        }

        // Phone format validation: show warning when value exists but invalid (doesn't block editing)
        if (field.type === 'phone') {
          const value = formValues[field.name];
          if (value && typeof value === 'string') {
            const digits = value.replace(/\D/g, '');
            if (digits.length > 0 && digits.length !== 10) {
              errors[field.name] = 'Please enter a valid phone number';
            }
          }
        }
      });
    });

    return errors;
  };

  // Check if form is complete (all required fields filled)
  const isFormComplete = useCallback(() => {
    const errors = validateForm();
    return Object.keys(errors).length === 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, combinedFormDefinition]);

  // Get form completion status
  const getFormCompletionStatus = () => {
    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return { isComplete: false, missingFields: [], missingFieldDetails: [] };
    }

    const missingFields = [];
    const missingFieldDetails = [];
    
    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;

      // Check section-level conditionals (same logic as FormRenderer)
      if (section.conditional) {
        const conditionalField = section.conditional.field;
        const conditionalValue = section.conditional.value;

        // The conditional field should already be prefixed by combineFormInstances
        // But check both prefixed and unprefixed versions for safety
        let currentValue = formValues[conditionalField];
        if (currentValue === undefined && section.form_instance_id) {
          // Try with instance ID prefix if not found
          const prefixedConditionalField = `${section.form_instance_id}_${conditionalField}`;
          currentValue = formValues[prefixedConditionalField];
        }

        // If conditional value is a string, convert to array for includes check
        const valuesToCheck = Array.isArray(conditionalValue) ? conditionalValue : [conditionalValue];

        // Skip section if conditional is not met
        if (!valuesToCheck.includes(currentValue)) {
          return; // Skip entire section
        }
      }

      const sectionMissingFields = [];
      
      section.fields.forEach(field => {
        if (field.admin_only) return;
        
        // Check visibility
        let isVisible = true;
        if (field.conditional) {
          const conditionalField = field.conditional.field;
          const conditionalValue = field.conditional.value;
          // The conditional field should already be prefixed by combineFormInstances
          // But check both prefixed and unprefixed versions for safety
          let currentValue = formValues[conditionalField];
          if (currentValue === undefined && field.form_instance_id) {
            // Try with instance ID prefix if not found
            const prefixedConditionalField = `${field.form_instance_id}_${conditionalField}`;
            currentValue = formValues[prefixedConditionalField];
          }
          isVisible = Array.isArray(conditionalValue)
            ? conditionalValue.includes(currentValue)
            : conditionalValue === currentValue;
        }

        if (!isVisible) return;

        // Check required
        const isRequired = field.required || 
          (field.conditional_required && isVisible);
        
        if (isRequired) {
          const value = formValues[field.name];
          let isEmpty = false;

          if (field.type === 'checkbox') {
            isEmpty = !value || !Array.isArray(value) || value.length === 0;
          } else if (field.type === 'signature') {
            isEmpty = !value ||
              (typeof value === 'string' && value.trim() === '') ||
              (typeof value === 'object' && !value.image);
          } else if (field.type === 'date') {
            isEmpty = !value || value.trim() === '';
          } else {
            isEmpty = !value || (typeof value === 'string' && value.trim() === '');
          }

          if (isEmpty) {
            missingFields.push(field.label);
            sectionMissingFields.push({ label: field.label, name: field.name });
          }
        }
      });

      // Group by section for display
      if (sectionMissingFields.length > 0) {
        const baseSectionId = section.section_name ? section.section_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : 'unknown-section';
        // Make section ID unique by including form instance ID to avoid conflicts between forms
        const sectionId = section.form_instance_id ? `${baseSectionId}-${section.form_instance_id}` : baseSectionId;
        missingFieldDetails.push({
          sectionName: section.section_name,
          fields: sectionMissingFields,
          sectionId: sectionId
        });
      }
    });

    return {
      isComplete: missingFields.length === 0,
      missingFields,
      missingFieldDetails
    };
  };

  const handleFormChange = (newValues) => {
    // Mark that user has started filling out the form
    if (!hasUserStartedFilling) {
      setHasUserStartedFilling(true);
    }

    // Detect which field(s) changed by comparing with previous values
    const previousValues = previousFormValuesRef.current;
    const changedFields = [];
    
    // Find all changed fields
    Object.keys(newValues).forEach(fieldName => {
      const oldValue = previousValues[fieldName];
      const newValue = newValues[fieldName];
      
      // Compare values (handle arrays for checkboxes)
      if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        if (JSON.stringify(oldValue.sort()) !== JSON.stringify(newValue.sort())) {
          changedFields.push(fieldName);
        }
      } else if (oldValue !== newValue) {
        changedFields.push(fieldName);
      }
    });
    
    // Also check for removed fields
    Object.keys(previousValues).forEach(fieldName => {
      if (!(fieldName in newValues)) {
        changedFields.push(fieldName);
      }
    });
    
    // Merge with previous values to avoid losing fields due to FormRenderer race
    // (e.g. grade can be lost when user quickly selects gender before re-render completes)
    let syncedValues = { ...previousFormValuesRef.current, ...newValues };

    // CRITICAL: Preserve all helper form fields - never let them be overwritten with empty
    const helperFormInstance = allFormInstances.find(inst =>
      (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
    );
    if (helperFormInstance) {
      const hasValue = (v) => v !== null && v !== undefined && v !== '' &&
        v !== 'Please select an option' &&
        !(Array.isArray(v) && v.length === 0);
      [previousFormValuesRef.current, formValues].forEach(source => {
        Object.keys(source || {}).forEach(key => {
          if (key.startsWith(`${helperFormInstance.id}_`) && hasValue(source[key])) {
            if (!hasValue(syncedValues[key])) {
              syncedValues = { ...syncedValues, [key]: source[key] };
            }
          }
        });
      });
    }
    if (formInstances.length > 0 && Object.keys(formInstanceMap).length > 0) {
      changedFields.forEach(changedFieldName => {
        const newValue = newValues[changedFieldName];
        const syncUpdates = syncSharedFields(
          changedFieldName,
          newValue,
          syncedValues,
          formInstanceMap,
          formInstances
        );
        
        // Merge sync updates into syncedValues
        syncedValues = {
          ...syncedValues,
          ...syncUpdates
        };
      });
    }
    
    // Track helper form data for autofill - only update changed fields, never rebuild from syncedValues
    // (rebuilding caused all helper form fields to be deleted when moving between fields)
    const updatedHelperData = { ...helperFormData };
    let helperDataChanged = false;

    // Track deleted authorized persons (repeatable group field name pattern)
    changedFields.forEach(changedFieldName => {
      if (changedFieldName.includes('repeating_group_4')) {
        const oldValue = previousValues[changedFieldName];
        const newValue = syncedValues[changedFieldName];
        
        if (Array.isArray(oldValue) && Array.isArray(newValue) && newValue.length < oldValue.length) {
          // Someone was deleted - track their name
          const oldNames = new Set(
            oldValue.map(person => 
              `${person.authorized_person_first_name || ''} ${person.authorized_person_last_name || ''}`.trim()
            )
          );
          const newNames = new Set(
            newValue.map(person => 
              `${person.authorized_person_first_name || ''} ${person.authorized_person_last_name || ''}`.trim()
            )
          );
          
          // Find deleted names
          oldNames.forEach(name => {
            if (!newNames.has(name) && name) {
              setDeletedAuthorizedPersons(prev => new Set([...prev, name]));
            }
          });
        }
      }
    });

    // Check if any changed fields belong to the helper form
    changedFields.forEach(changedFieldName => {
      // Extract the original field name from the prefixed field name
      const parts = changedFieldName.split('_');
      if (parts.length >= 2) {
        const instanceId = parts[0];
        const originalFieldName = parts.slice(1).join('_');

        // Find the form instance to check if it's the helper form
        const instance = allFormInstances.find(inst => inst.id === instanceId);
        if (instance && (instance.instance_name || instance.master_form_name || '').toLowerCase().includes('helper')) {
          const newValue = syncedValues[changedFieldName];
          
          // CRITICAL: Track that this helper form field was manually changed by the user
          setManuallyChangedHelperFields(prev => new Set([...prev, originalFieldName]));
          
          // CRITICAL: Never update student_age in helperData from synced values
          if (originalFieldName !== 'student_age') {
            if (newValue !== null && newValue !== undefined && newValue !== '' && newValue !== 'Please select an option') {
              updatedHelperData[originalFieldName] = newValue;
              helperDataChanged = true;
            }
            // NEVER delete helper form fields when we get empty - preserves first/last name, age when
            // selecting grade (FormRenderer can send stale/partial data that would incorrectly clear them)
          } else {
            // For student_age field, preserve the existing helperData value - don't update it
          }
        }
      }
    });

    // Update helper form data if it changed
    if (helperDataChanged) {
      setHelperFormData(updatedHelperData);
    }

    // CRITICAL: Store latest values in ref BEFORE setState - ensures autofill useEffect
    // sees user's changes even if it runs before state commits
    latestFormValuesRef.current = syncedValues;

    // Preserve grade field value - use syncedValues (current change), NOT formValues (stale)
    // BUG FIX: Using formValues here was reverting user's grade change to the previous value
    const gradeFieldKey = Object.keys(syncedValues).find(key => {
      const isUnprefixedGrade = key === 'grade';
      const isPrefixedGrade = key.endsWith('_grade') && 
        !key.includes('previous_school_grade') &&
        !key.includes('previous_enrollment_grade') && 
        !key.includes('retained_grade') &&
        !key.includes('sibling_grade');
      return isUnprefixedGrade || isPrefixedGrade;
    });
    const preservedGradeValue = gradeFieldKey ? syncedValues[gradeFieldKey] : null;

    // Check if conditional field changed to "Yes" - trigger autofill
    let shouldTriggerAutofill = false;
    changedFields.forEach(changedFieldName => {
      const parts = changedFieldName.split('_');
      if (parts.length >= 2) {
        const originalFieldName = parts.slice(1).join('_');
        if (originalFieldName === 'has_records_from_previous_school') {
          const newValue = syncedValues[changedFieldName];
          const oldValue = previousFormValuesRef.current[changedFieldName];
          if (newValue === 'Yes' && oldValue !== 'Yes') {
            shouldTriggerAutofill = true;
          }
        }
      }
    });

    // Ensure grade field is preserved in synced values
    if (gradeFieldKey && preservedGradeValue !== null && preservedGradeValue !== undefined && preservedGradeValue !== '') {
      syncedValues[gradeFieldKey] = preservedGradeValue;
    }

    // Update form values with synced values
    setFormValues(syncedValues);

    // Trigger autofill if conditional field changed to "Yes"
    if (shouldTriggerAutofill && Object.keys(helperFormData).length > 0 && combinedFormDefinition) {
      const autofilledValues = applyHelperFormAutofill(syncedValues, helperFormData, helperFormData);
      // Preserve grade field in autofilled values too
      if (gradeFieldKey && preservedGradeValue !== null && preservedGradeValue !== undefined && preservedGradeValue !== '') {
        autofilledValues[gradeFieldKey] = preservedGradeValue;
      }
      
      // CRITICAL: Preserve ALL manually changed helper form fields - never let autofill overwrite them
      const helperFormInstance = allFormInstances.find(inst => 
        (inst.instance_name || inst.master_form_name || '').toLowerCase().includes('helper')
      );
      if (helperFormInstance) {
        manuallyChangedHelperFields.forEach(fieldName => {
          const helperFormFieldKey = `${helperFormInstance.id}_${fieldName}`;
          const currentValue = syncedValues[helperFormFieldKey];
          // If helper form has a manually changed value, preserve it - don't let autofill overwrite it
          if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
            autofilledValues[helperFormFieldKey] = currentValue;
            console.log(`[NewStudentForm] Preserving manually changed helper form field in conditional autofill: ${fieldName} = ${currentValue}`);
          }
        });
      }
      
      if (JSON.stringify(autofilledValues) !== JSON.stringify(syncedValues)) {
        setFormValues(autofilledValues);
      }
    }

    // Update ref for next comparison
    previousFormValuesRef.current = syncedValues;
    
    // Clear validation errors when user makes changes
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }
  };

  const handleDeleteProgress = async () => {
    try {
      // First get the progress to find its ID
      const progressResponse = await axios.get(`/api/form-submissions/progress?student_intake_id=${studentIntakeId}`);
      const combinedFormProgress = progressResponse.data.find(progress =>
        progress.form_instance_id === null && !progress.is_complete
      );


      if (combinedFormProgress) {
        // Delete the specific progress by ID
        await axios.delete(`/api/form-submissions/progress/${combinedFormProgress.id}`);
      } else {
      }

      setFormValues({});

      // Clear the submission flag since user is restarting
      if (studentIntakeId) {
        sessionStorage.removeItem(`student_submitted_${studentIntakeId}`);
      }

      // Notify parent dashboard about progress update (progress deleted)
      window.dispatchEvent(new CustomEvent('progressUpdated'));
      setHasLoadedProgress(false);
      setHasUserStartedFilling(false);
      setShowDeleteConfirm(false);
      setSuccess(false);
      setError('');
      // Navigate away from the form
      navigate('/parent/students');
    } catch (error) {
      console.error('Failed to delete progress:', error);
      setError('Failed to delete form progress');
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const completionStatus = getFormCompletionStatus();
      const errorMessage = completionStatus.missingFields.length > 0
        ? `Please complete all required fields. Missing: ${completionStatus.missingFields.slice(0, 5).join(', ')}${completionStatus.missingFields.length > 5 ? '...' : ''}`
        : 'Please fill in all required fields';
      setError(errorMessage);
      
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      
      return;
    }

    setLoading(true);

    try {
      // Extract student info from form data
      const studentInfo = extractStudentInfo(formValues, formInstanceMap, formInstances);
      
      if (!studentInfo || !studentInfo.first_name || !studentInfo.last_name) {
        setError('Student name is required. Please fill in the student information section.');
        setLoading(false);
        return;
      }

      // Split form data by instance
      const splitData = splitFormDataByInstance(formValues, formInstanceMap, formInstances);
      
      // Prepare form_instances_data for submission and collect helper data
      const formInstancesData = {};

      formInstances.forEach(instance => {
        const instanceData = splitData[instance.id];

        if (instanceData) {
          const isHelperForm = (instance.instance_name || instance.master_form_name || '').includes('Helper Form');

          if (isHelperForm) {
            // Collect helper data to save at parent level
          } else {
            // Check for repeatable group data (sections with allows_multiple)
            const repeatableGroupKeys = Object.keys(instanceData).filter(k => k.includes('repeating_group_'));
            if (repeatableGroupKeys.length > 0) {
              console.log(`[NewStudentForm] Found repeatable group data in ${instance.instance_name || instance.master_form_name}:`, {
                keys: repeatableGroupKeys,
                sampleData: repeatableGroupKeys.map(k => ({ key: k, value: instanceData[k], isArray: Array.isArray(instanceData[k]), length: Array.isArray(instanceData[k]) ? instanceData[k].length : null }))
              });
            }
            
            // Regular form instance
            formInstancesData[instance.id] = {
              student_name: `${studentInfo.first_name} ${studentInfo.last_name}`,
              data: instanceData,
              allows_multiple: instance.form_data?.allows_multiple || false
            };
            
            console.log(`[NewStudentForm] Prepared form instance data for ${instance.instance_name || instance.master_form_name}:`, {
              instanceId: instance.id,
              dataKeys: Object.keys(instanceData),
              dataKeyCount: Object.keys(instanceData).length,
              hasRepeatableGroups: repeatableGroupKeys.length > 0,
              allows_multiple: instance.form_data?.allows_multiple || false
            });
          }
        }
      });

      // Submit to form-submissions endpoint (which will also create/update student)
      const submitData = {
        submission_data: formValues,
        form_instances_data: formInstancesData,
        student_info: studentInfo
      };

      // Include student intake ID if available
      if (studentIntakeId) {
        submitData.student_intake_id = studentIntakeId;
      }


      await axios.post('/api/form-submissions/submit', submitData, { timeout: 30000 });

      // Mark this student as submitted to prevent showing empty form on refresh
      if (studentIntakeId) {
        sessionStorage.setItem(`student_submitted_${studentIntakeId}`, 'true');
      }

      // Clear saved progress after successful submission (non-blocking)
      // Don't wait for this to complete before navigating
      axios.post('/api/form-submissions/save-progress', {
        submission_data: {},
        progress_percentage: 0,
        student_intake_id: studentIntakeId // Include student intake ID for clearing
      }).then(() => {
        // Notify parent dashboard about progress update (form completed)
        window.dispatchEvent(new CustomEvent('progressUpdated'));
      }).catch(error => {
        console.error('Failed to clear saved progress:', error);
        // Don't block navigation if this fails
      });

      // Clear form data immediately after successful submission
      setFormValues({});
      setHelperFormData({});
      setHouseholdInformation({});
      setManuallyChangedHelperFields(new Set());
      setSuccess(true);
      setLoading(false); // Clear loading state to show success

      // Navigate with success message - use setTimeout to ensure state updates complete
      const studentName = `${studentInfo.first_name} ${studentInfo.last_name}`;
      setTimeout(() => {
        navigate(`/parent/student-intake?success=true&studentName=${encodeURIComponent(studentName)}`);
      }, 100);
    } catch (error) {
      console.error('Form submission error:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setError(error.response?.data?.error || 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !combinedFormDefinition) {
    return (
      <div className="add-district">
        <h2>New Student - {studentIntake?.first_name || 'Loading...'}</h2>
        <div>Loading forms...</div>
      </div>
    );
  }

  if (!combinedFormDefinition) {
    return (
      <div className="add-district">
        <h2>New Student - {studentIntake?.first_name || 'Loading...'}</h2>
        {error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div>No forms available</div>
        )}
      </div>
    );
  }

  return (
    <div className="add-district">
      <h2>New Student - {studentIntake?.first_name || 'Loading...'}</h2>

      {/* Progress Bar with Save Button */}
      <div style={{
        background: '#ff9800',
        color: 'white',
        padding: '0.75rem 1.5rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <strong>Form Progress:</strong> {calculateProgress()}% complete
          {lastSavedTime && (
            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', opacity: 0.8 }}>
              Last saved: {lastSavedTime.toLocaleDateString()} {lastSavedTime.toLocaleTimeString()}
            </div>
          )}
        </div>
        <button
          onClick={saveProgress}
          disabled={savingProgress}
          style={{
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '1px solid white',
            padding: '0.4rem 0.8rem',
            borderRadius: '4px',
            cursor: savingProgress ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem'
          }}
        >
          {savingProgress ? 'Saving...' : 'Save Progress'}
        </button>
      </div>

      {hasLoadedProgress && (
        <div style={{ 
          background: '#d1ecf1', 
          color: '#0c5460', 
          padding: '1rem', 
          borderRadius: '4px', 
          marginBottom: '1rem', 
          border: '1px solid #bee5eb',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>✓</span>
          <div>
            <strong>Saved Progress Loaded</strong> - Your previous form data has been restored. You can continue where you left off.
          </div>
        </div>
      )}
      {savingProgress && (
        <div style={{ background: '#e3f2fd', color: '#1976d2', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem' }}>
          Saving progress...
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {success && (
        <div style={{ background: '#dfd', color: '#3a3', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          Student information submitted successfully!
        </div>
      )}
      {siblingAutofillMessage && (
        <div style={{ background: '#f0f4ff', color: '#1c3d5a', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', border: '1px solid #c3d8ff' }}>
          {siblingAutofillMessage}
        </div>
      )}
      {(() => {
        const completionStatus = getFormCompletionStatus();
        if (!completionStatus.isComplete && Object.keys(formValues).length > 0) {
          return (
            <div style={{
              background: '#fff3cd',
              color: '#856404',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              border: '1px solid #ffc107'
            }}>
              <strong>Form Incomplete:</strong> Please fill in all required fields before submitting.
              {completionStatus.missingFieldDetails.length > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Missing required fields by section:</strong>
                  </div>
                  {completionStatus.missingFieldDetails.slice(0, 2).map((section, idx) => (
                    <div key={idx} style={{ marginBottom: '0.25rem' }}>
                      <strong>{section.sectionName}:</strong>{' '}
                      {section.fields.slice(0, 2).map((field, fieldIdx) => (
                        <button
                          key={fieldIdx}
                          onClick={() => {
                            const element = document.getElementById(section.sectionId);
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#007bff',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            padding: '0',
                            fontSize: 'inherit'
                          }}
                        >
                          {field.label}
                        </button>
                      ))}
                      {section.fields.length > 2 && ` (+${section.fields.length - 2} more)`}
                      {idx < completionStatus.missingFieldDetails.slice(0, 2).length - 1 && '; '}
                    </div>
                  ))}
                  {completionStatus.missingFieldDetails.length > 2 && (
                    <div>... and {completionStatus.missingFieldDetails.length - 2} more sections</div>
                  )}
                </div>
              )}
            </div>
          );
        }
        return null;
      })()}
      <form onSubmit={handleSubmit}>
        <FormRenderer
          formData={combinedFormDefinition}
          formValues={formValues}
          onChange={handleFormChange}
          errors={validationErrors}
          autofillEnabled={autofillEnabled}
          onAutofillToggle={(instanceId, enabled) => {
            setAutofillEnabled(prev => ({
              ...prev,
              [instanceId]: enabled
            }));
          }}
          userRole="parent"
          isFieldReadOnly={isFieldReadOnly}
          onAddSiblingsFromList={siblings.length > 0 ? handleAddFromStudentList : null}
        />
        <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={loading || !isFormComplete()}
              title={!isFormComplete() ? 'Please complete all required fields before submitting' : ''}
            >
              {loading ? 'Submitting...' : 'Submit All Forms'}
            </button>
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={async () => {
                // Save progress before navigating
                await saveProgress();
                navigate('/parent/student-intake');
              }}
            >
              Save for Later
            </button>
          </div>
          <button 
            type="button" 
            className="btn-danger" 
            onClick={() => setShowDeleteConfirm(true)}
            style={{ background: '#dc3545', color: 'white', border: 'none' }}
          >
            Cancel Form
          </button>
        </div>

        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}>
              <h3 style={{ marginTop: 0 }}>Delete Form Progress?</h3>
              <p style={{ marginBottom: '1rem' }}>
                Are you sure you want to delete all your form progress? This action cannot be undone.
              </p>
              <div style={{ 
                background: '#fff3cd', 
                border: '1px solid #ffc107', 
                borderRadius: '4px', 
                padding: '1rem', 
                marginBottom: '1rem' 
              }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
                  <strong>Tip:</strong> If you want to save your progress and continue later, 
                  just close this page or click "Save for Later". Your progress is automatically saved 
                  every 30 seconds and will be available when you return.
                </p>
              </div>
              <div className="modal-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setShowDeleteConfirm(false)} 
                  className="btn-secondary"
                >
                  Keep Progress
                </button>
                <button 
                  onClick={handleDeleteProgress} 
                  className="btn-danger"
                  style={{ background: '#dc3545', color: 'white', border: 'none' }}
                >
                  Delete Progress
                </button>
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Fixed Bottom Action Bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '2px solid #e0e0e0',
        padding: '1rem 2rem',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '2rem'
      }}>
        {/* Missing Fields List */}
        <div style={{ flex: 1, maxWidth: '60%' }}>
          {(() => {
            const completionStatus = getFormCompletionStatus();
            if (completionStatus.missingFields.length > 0) {
              return (
                <div>
                  <div style={{
                    color: '#e74c3c',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    marginBottom: '0.5rem'
                  }}>
                    Required Fields to Complete ({completionStatus.missingFields.length}):
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#666',
                    maxHeight: '4rem',
                    overflow: 'hidden'
                  }}>
                    {completionStatus.missingFieldDetails.map((section, idx) => (
                      <span key={idx}>
                        <strong>{section.sectionName}:</strong>{' '}
                        {section.fields.slice(0, 3).map((field, fieldIdx) => (
                          <button
                            key={fieldIdx}
                            onClick={() => {
                              // Try to scroll to the specific field first (field container)
                              let fieldElement = document.getElementById(`field-${field.name}`);
                              if (!fieldElement) {
                                // Fall back to finding the input element by name
                                fieldElement = document.querySelector(`[name="${field.name}"]`);
                              }
                              if (fieldElement) {
                                fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                // Also highlight the field briefly
                                fieldElement.style.boxShadow = '0 0 0 3px #007bff';
                                setTimeout(() => {
                                  fieldElement.style.boxShadow = '';
                                }, 2000);
                              } else {
                                // Fall back to scrolling to the section
                                const element = document.getElementById(section.sectionId);
                                if (element) {
                                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#007bff',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: '0',
                              fontSize: 'inherit',
                              marginRight: '0.25rem'
                            }}
                          >
                            {field.label}
                          </button>
                        ))}
                        {section.fields.length > 3 && `(+${section.fields.length - 3})`}
                        {idx < completionStatus.missingFieldDetails.length - 1 && '; '}
                      </span>
                    ))}
                  </div>
                </div>
              );
            } else {
              return (
                <div style={{
                  color: '#27ae60',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}>
                  ✓ All required fields completed!
                </div>
              );
            }
          })()}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            type="button"
            onClick={saveProgress}
            disabled={savingProgress}
            style={{
              background: '#f39c12',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: savingProgress ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              opacity: savingProgress ? 0.6 : 1,
              transition: 'opacity 0.2s'
            }}
          >
            {savingProgress ? 'Saving...' : 'Save Progress'}
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !getFormCompletionStatus().isComplete}
            style={{
              background: getFormCompletionStatus().isComplete ? '#27ae60' : '#bdc3c7',
              color: 'white',
              border: 'none',
              padding: '0.75rem 2rem',
              borderRadius: '6px',
              cursor: (loading || !getFormCompletionStatus().isComplete) ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              opacity: (loading || !getFormCompletionStatus().isComplete) ? 0.6 : 1,
              transition: 'all 0.2s'
            }}
            title={!getFormCompletionStatus().isComplete ? 'Please complete all required fields before submitting' : ''}
          >
            {loading ? 'Submitting...' : 'Submit Form'}
          </button>

          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
            title="Scroll to top of the page"
          >
            ↑ Back to Top
          </button>
        </div>
      </div>

      {/* Add padding to main content to prevent overlap with fixed bottom bar */}
      <div style={{ height: '100px' }}></div>
    </div>
  );
};

export default NewStudentForm;
